// noteMarkdown — turn the Markdown a capture surface holds into the HTML the
// notes feed renders. Pure: no DOM, no network.
//
// WHY THIS EXISTS. The Notes page (pages/GlobalNotes.jsx) used to file each
// routed section through `<p>${text}</p>` with `\n` → `<br/>`, so a bullet list
// or a heading somebody typed arrived on the deal as literal "- item" / "## Title"
// text — a note with none of the structure the deal's own composer gives every
// other note. The compose box and the note rows now run the same BlockNote
// editor the deal notes use and file its JSON directly; this converter is the
// FALLBACK for a row whose editor never reported (and the whole path on iOS,
// whose capture screen is a plain TextInput — mobile/src/util/noteMarkdown.ts is
// the mirror; KEEP THE TWO IN LOCKSTEP).
//
// Deliberately small: headings, bullet / numbered / checklist items, block
// quotes, paragraphs, and bold / italic / strikethrough inline. Anything else is
// text. It never throws and never returns an empty string for non-empty input.

export const escapeHtml = (s) => String(s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Inline marks over ALREADY-ESCAPED text, so a literal `<b>` in the note stays
// text while `**bold**` becomes markup. Bold before italic so `**x**` is not
// read as an italic wrapped in stray stars.
export const inlineMarkdownToHtml = (escaped) => String(escaped || '')
  .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  .replace(/__(.+?)__/g, '<strong>$1</strong>')
  .replace(/(^|[^*\w])\*([^*\n]+?)\*(?!\w)/g, '$1<em>$2</em>')
  .replace(/(^|[^_\w])_([^_\n]+?)_(?!\w)/g, '$1<em>$2</em>')
  .replace(/~~(.+?)~~/g, '<s>$1</s>');

const inline = (raw) => inlineMarkdownToHtml(escapeHtml(raw));

const HEADING  = /^\s{0,3}(#{1,3})\s+(.+?)\s*#*\s*$/;
const CHECK    = /^\s*[-*+•]\s+\[([ xX])\]\s+(.*)$/;
const BULLET   = /^\s*[-*+•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const QUOTE    = /^\s*>\s?(.*)$/;

/**
 * Markdown (or plain text — which is Markdown with no marks) → note HTML.
 * Consecutive plain lines fold into one paragraph joined by <br/>, a blank
 * line ends a paragraph, and a list item ends any open paragraph.
 */
export function markdownToNoteHtml(markdown) {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n');
  const out = [];
  let para = [];       // pending plain lines
  let list = null;     // { tag: 'ul'|'ol', items: [] }

  const flushPara = () => {
    if (!para.length) return;
    out.push(`<p>${para.map(inline).join('<br/>')}</p>`);
    para = [];
  };
  const flushList = () => {
    if (!list) return;
    out.push(`<${list.tag}>${list.items.map(i => `<li>${i}</li>`).join('')}</${list.tag}>`);
    list = null;
  };
  const pushItem = (tag, html) => {
    flushPara();
    if (list && list.tag !== tag) flushList();
    if (!list) list = { tag, items: [] };
    list.items.push(html);
  };

  for (const line of lines) {
    if (!line.trim()) { flushPara(); flushList(); continue; }
    let m;
    if ((m = HEADING.exec(line))) {
      flushPara(); flushList();
      out.push(`<h${m[1].length}>${inline(m[2])}</h${m[1].length}>`);
    } else if ((m = CHECK.exec(line))) {
      const done = m[1].toLowerCase() === 'x';
      pushItem('ul', `${done ? '☑' : '☐'} ${inline(m[2])}`);
    } else if ((m = BULLET.exec(line))) {
      pushItem('ul', inline(m[1]));
    } else if ((m = NUMBERED.exec(line))) {
      pushItem('ol', inline(m[1]));
    } else if ((m = QUOTE.exec(line))) {
      flushPara(); flushList();
      out.push(`<blockquote>${inline(m[1])}</blockquote>`);
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara(); flushList();
  return out.join('');
}
